import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  DocumentArrowDownIcon,
  DocumentTextIcon,
  PencilIcon,
  XMarkIcon,
  ClockIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { transactionApi } from '../../api/transactionApi';
import { customerApi } from '../../api/customerApi';
import { generateLedgerInvoice, generateTransactionInvoice } from '../../utils/generateInvoice';
import { useAuth } from '../../hooks/useAuth';
import { paymentApi } from '../../api/paymentApi';

const Ledger = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // For Edit History Modal
  const [selectedHistory, setSelectedHistory] = useState(null);
  
  // For Attachment Viewer Modal
  // For Attachment Viewer Modal
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  
  // For Payment Request Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [requestingPayment, setRequestingPayment] = useState(false);
  
  const { activeBusiness } = useAuth();

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customerRes, ledgerRes] = await Promise.all([
        customerApi.getCustomerDetails(id),
        transactionApi.getLedger(id)
      ]);
      setCustomer(customerRes.data.data);
      setTransactions(ledgerRes.data.data);
    } catch (error) {
      console.error('Error fetching ledger:', error);
      toast.error('Failed to load ledger data');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!customer || !transactions) return;
    try {
      generateLedgerInvoice(transactions, activeBusiness, customer);
      toast.success('Ledger PDF generated successfully!', { icon: '📄' });
    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleDownloadReceipt = (e, tx) => {
    e.stopPropagation();
    try {
      generateTransactionInvoice(tx, activeBusiness, customer);
      toast.success('Receipt PDF generated successfully!', { icon: '🧾' });
    } catch (error) {
      console.error('PDF Generation Error:', error);
      toast.error('Failed to generate Receipt');
    }
  };

  const [customerEmail, setCustomerEmail] = useState('');

  const handleSendReminder = async (e) => {
    e.preventDefault();
    if (!paymentAmount || Number(paymentAmount) <= 0) return;
    
    try {
      setRequestingPayment(true);
      const res = await paymentApi.sendReminder({
        customerId: id,
        amount: Number(paymentAmount),
        email: customerEmail
      });
      if (res.data.success) {
        toast.success('Reminder sent successfully! (Check terminal for mock link if email is not setup)', { duration: 5000 });
        setShowPaymentModal(false);
        setPaymentAmount('');
        if(res.data.paymentLink) {
          console.log("Mock Payment Link:", res.data.paymentLink);
        }
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || 'Failed to send reminder';
      toast.error(msg);
    } finally {
      setRequestingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#dc343b]"></div>
      </div>
    );
  }

  const currentBalance = customer?.currentBalance || 0;

  // Determine theme colors based on balance
  let bannerClass = 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700';
  let textClass = 'text-gray-900 dark:text-white';
  let label = 'Settled';
  let labelClass = 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300';

  if (currentBalance > 0) {
    bannerClass = 'bg-[#f0fdf4] dark:bg-green-900/20 border-green-200 dark:border-green-800/50';
    textClass = 'text-[#16a34a] dark:text-green-400';
    label = 'You Will Get';
    labelClass = 'bg-[#dcfce7] dark:bg-green-900/40 text-[#16a34a] dark:text-green-400';
  } else if (currentBalance < 0) {
    bannerClass = 'bg-[#fef2f2] dark:bg-red-900/20 border-red-200 dark:border-red-800/50';
    textClass = 'text-[#ef4444] dark:text-red-400';
    label = 'You Will Give';
    labelClass = 'bg-[#fee2e2] dark:bg-red-900/40 text-[#ef4444] dark:text-red-400';
  }

  return (
    <div className="w-full h-full flex flex-col items-center">
      
      <div className="w-full max-w-full space-y-5">
        
        {/* Top Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <Link 
              to="/customers"
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">{customer?.name}</h1>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 font-medium">
                {customer?.phone}
              </p>
            </div>
          </div>
          <button 
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg font-bold text-[13px] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors shadow-sm"
          >
            <DocumentArrowDownIcon className="h-4 w-4" />
            <span>Report</span>
          </button>
        </div>

        {/* Dynamic Net Balance Banner */}
        <div className={`w-full rounded-xl border px-6 py-6 flex flex-col sm:flex-row items-center justify-between shadow-sm transition-all ${bannerClass}`}>
          <div className="mb-4 sm:mb-0 text-center sm:text-left">
            <p className="text-[13px] uppercase font-bold tracking-widest text-gray-500 mb-2">Net Balance</p>
            <span className={`text-[12px] font-bold px-3 py-1 rounded-md uppercase tracking-wide ${labelClass}`}>
              {label}
            </span>
          </div>
          <div className="text-center sm:text-right">
            <p className={`text-4xl sm:text-[2rem] font-semibold tracking-tight ${textClass}`}>
              ₹{Math.abs(currentBalance).toLocaleString()}
            </p>
            {currentBalance > 0 && (
              <button 
                onClick={() => {
                  setPaymentAmount(Math.abs(currentBalance));
                  setCustomerEmail(customer?.email || '');
                  setShowPaymentModal(true);
                }}
                className="mt-3 w-full sm:w-auto bg-[#16a34a] text-white px-4 py-2 rounded-lg font-bold text-[13px] hover:bg-[#15803d] transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                🔔 Send Reminder
              </button>
            )}
          </div>
        </div>

        {/* Ledger Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-colors">
          
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-2 bg-[#f8fafc] dark:bg-gray-900 px-5 py-4 border-b border-gray-200 dark:border-gray-700 text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
            <div className="col-span-6">Entries</div>
            <div className="col-span-3 text-right text-red-500 dark:text-red-400">You Gave</div>
            <div className="col-span-3 text-right text-green-600 dark:text-green-500">You Got</div>
          </div>
          
          {/* Entries */}
          <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
            
            {/* Opening Balance */}
            {customer?.openingBalance !== 0 && (
              <div className="grid grid-cols-12 gap-2 px-5 py-5 bg-[#fdfdfd] dark:bg-gray-800 items-center">
                <div className="col-span-6">
                  <p className="text-[14px] font-bold text-gray-900 dark:text-white">Opening Balance</p>
                  <p className="text-[12px] text-gray-400 dark:text-gray-500 mt-0.5">At time of creation</p>
                </div>
                <div className="col-span-3 text-right">
                  {customer?.openingBalance > 0 && (
                    <span className="text-[15px] font-bold text-red-500">
                      ₹{customer.openingBalance.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className="col-span-3 text-right">
                  {customer?.openingBalance < 0 && (
                    <span className="text-[15px] font-bold text-green-600">
                      ₹{Math.abs(customer.openingBalance).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            )}

            {transactions.length > 0 ? (
              transactions.map((tx) => {
                const isEdited = tx.editHistory && tx.editHistory.length > 0;
                
                return (
                  <div 
                    key={tx._id} 
                    className="grid grid-cols-12 gap-2 px-5 py-5 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors items-center group relative cursor-pointer" 
                    onClick={() => navigate(`/customers/${id}/transaction?type=${tx.type}&edit=true&transactionId=${tx._id}`)}
                  >
                    <div className="col-span-6">
                      <p className="text-[14px] font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                        {tx.description || 'Transaction'}
                        {tx.attachments && tx.attachments.length > 0 && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAttachment(tx.attachments[0]);
                            }}
                            className="text-gray-400 hover:text-blue-600 transition-colors bg-gray-100 dark:bg-gray-700 rounded-full p-1 ml-1"
                            title="View Attachment"
                          >
                            {tx.attachments[0].type === 'pdf' ? (
                              <DocumentTextIcon className="h-3.5 w-3.5" />
                            ) : (
                              <PhotoIcon className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[12px] font-medium text-gray-400">
                          {new Date(tx.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute:'2-digit' })}
                        </p>
                        {isEdited && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); setSelectedHistory(tx.editHistory); }}
                            className="text-[10px] bg-gray-100 text-gray-600 font-bold px-1.5 py-0.5 rounded flex items-center gap-1 hover:bg-gray-200"
                            title="Click to view edit history"
                          >
                            <ClockIcon className="h-3 w-3" />
                            Edited
                          </button>
                        )}
                        <Link 
                          to={`/customers/${id}/transaction?type=${tx.type}&edit=true&transactionId=${tx._id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="ml-2 text-gray-300 hover:text-blue-600 bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700 rounded-md p-1 opacity-0 group-hover:opacity-100 transition-all z-10"
                          title="Edit Transaction"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </Link>
                        <button 
                          onClick={(e) => handleDownloadReceipt(e, tx)}
                          className="ml-1 text-gray-300 hover:text-red-600 bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-700 rounded-md p-1 opacity-0 group-hover:opacity-100 transition-all z-10"
                          title="Download Receipt"
                        >
                          <DocumentArrowDownIcon className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    <div className="col-span-3 text-right">
                      {tx.type === 'credit' && (
                        <span className="text-[15px] font-bold text-red-500">
                          ₹{tx.amount.toLocaleString()}
                        </span>
                      )}
                    </div>
                    <div className="col-span-3 text-right">
                      {(tx.type === 'payment' || tx.type === 'debit') && (
                        <span className="text-[15px] font-bold text-green-600">
                          ₹{tx.amount.toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-16 text-center bg-gray-50/50">
                <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-[14px] font-bold text-gray-400">No transactions recorded yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Solid Sticky Bottom Action Bar using Links to the new page */}
      <div className="sticky bottom-0 bg-white dark:bg-[#0f172a] border-t border-gray-200 dark:border-gray-800 py-4 z-20 shadow-[0_-4px_15px_-3px_rgba(0,0,0,0.05)] w-full -mx-4 sm:-mx-6 lg:-mx-10 px-4 sm:px-6 lg:px-10 mt-auto flex justify-center transition-colors">
        <div className="w-full max-w-5xl flex gap-4">
          <Link
            to={`/customers/${id}/transaction?type=credit`}
            className="flex-1 bg-[#ef4444] text-white hover:bg-[#dc2626] py-3.5 rounded-xl font-extrabold text-[15px] transition-colors shadow-sm tracking-widest flex justify-center items-center"
          >
            YOU GAVE ₹
          </Link>
          <Link
            to={`/customers/${id}/transaction?type=payment`}
            className="flex-1 bg-[#16a34a] text-white hover:bg-[#15803d] py-3.5 rounded-xl font-extrabold text-[15px] transition-colors shadow-sm tracking-widest flex justify-center items-center"
          >
            YOU GOT ₹
          </Link>
        </div>
      </div>

      {/* Edit History Modal */}
      {selectedHistory && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setSelectedHistory(null)}></div>
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl relative z-10 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[16px] font-extrabold text-gray-900 flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-gray-500" />
                Edit History
              </h3>
              <button onClick={() => setSelectedHistory(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded-full transition-colors">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="max-h-[60vh] overflow-y-auto pr-2 space-y-6">
              {selectedHistory.map((history, index) => (
                <div key={index} className="relative pl-6 border-l-2 border-gray-200 last:border-0 pb-6 last:pb-0">
                  <div className="absolute -left-[9px] top-0 h-4 w-4 rounded-full bg-gray-200 border-2 border-white"></div>
                  <div className="-mt-1.5">
                    <p className="text-[12px] font-bold text-gray-500 mb-1">
                      {new Date(history.editedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[13px] text-gray-600 mb-1">
                        Amount was: <span className="font-bold text-gray-900">₹{history.previousAmount}</span>
                      </p>
                      <p className="text-[13px] text-gray-600 mb-1">
                        Type was: <span className="font-bold capitalize text-gray-900">{history.previousType}</span>
                      </p>
                      <p className="text-[13px] text-gray-600">
                        Details: <span className="font-bold text-gray-900">{history.previousDescription || 'None'}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )).reverse()}
            </div>
          </div>
        </div>
      )}

      {/* Attachment Viewer Modal */}
      {selectedAttachment && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/80 backdrop-blur-sm" onClick={() => setSelectedAttachment(null)}></div>
          <div className="bg-white rounded-xl overflow-hidden w-full max-w-4xl max-h-[90vh] shadow-2xl relative z-10 flex flex-col animate-in zoom-in-95 duration-200">
            
            <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-white">
              <h3 className="text-[15px] font-extrabold text-gray-900 flex items-center gap-2">
                {selectedAttachment.type === 'pdf' ? <DocumentTextIcon className="h-5 w-5 text-gray-500" /> : <PhotoIcon className="h-5 w-5 text-gray-500" />}
                {selectedAttachment.name || 'Attachment Preview'}
              </h3>
              <button onClick={() => setSelectedAttachment(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded-full transition-colors">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto bg-gray-100 p-4 flex justify-center items-center min-h-[50vh]">
              {selectedAttachment.type === 'pdf' ? (
                <iframe 
                  src={selectedAttachment.url} 
                  className="w-full h-[70vh] rounded-lg shadow-sm border border-gray-200 bg-white"
                  title="PDF Viewer"
                />
              ) : (
                <img 
                  src={selectedAttachment.url} 
                  alt="Attachment" 
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
                />
              )}
            </div>

            <div className="p-4 bg-white border-t border-gray-100 flex justify-end">
              <a 
                href={selectedAttachment.url} 
                target="_blank" 
                rel="noreferrer"
                className="px-5 py-2.5 bg-gray-900 text-white font-bold text-[13px] rounded-lg shadow-sm hover:bg-gray-800 transition-colors"
              >
                Open in New Tab
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Payment Request Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setShowPaymentModal(false)}></div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl relative z-10 animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[18px] font-bold text-gray-900 dark:text-white">Request Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSendReminder}>
              <div className="mb-4">
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Customer Email</label>
                <input
                  type="email"
                  required
                  placeholder="customer@email.com"
                  className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                />
              </div>

              <div className="mb-6">
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-2">Amount to Request (₹)</label>
                <input
                  type="number"
                  required
                  min="1"
                  className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[16px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors font-bold"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                />
                <p className="text-[12px] text-gray-500 mt-2">
                  An email with a secure Razorpay payment link will be sent to this customer.
                </p>
              </div>
              
              <button
                type="submit"
                disabled={requestingPayment}
                className="w-full bg-[#16a34a] hover:bg-[#15803d] text-white py-3.5 rounded-xl font-extrabold text-[15px] transition-colors shadow-sm disabled:opacity-70 flex justify-center items-center gap-2"
              >
                {requestingPayment ? 'Sending...' : '📨 Send Reminder Email'}
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Ledger;
